import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactCTA from '../components/ContactCTA';
import SEO from '../components/SEO';
import { companySchema, getPageMetadata, productCollectionSchema } from '../config/seo';

const ProductsPage = () => {
  const { language } = useLanguage();

  const getLocalizedText = (texts) => texts[language] || texts.tc;
  const meta = getPageMetadata('products', language);

  const categories = [
    {
      name_tc: '新鮮活魚',
      name_cn: '新鲜活鱼',
      name_en: 'Fresh / Chilled Fish',
      image: 'https://images.unsplash.com/photo-1615141982883-c7ad0e69fd62?w=800',
      products: [
        // Column 1
        [
          { tc: '挪威三文魚', cn: '挪威三文鱼', en: 'Norwegian Salmon' },
          { tc: '馬友', cn: '马友', en: 'Thread Fin' },
          { tc: '紅立', cn: '红立', en: 'Red Snapper' },
          { tc: '多寶(生)', cn: '多宝(生)', en: 'Turbot (Live)' },
          { tc: '鯇魚', cn: '鲩鱼', en: 'Grass Crap' },
          { tc: '黃立倉', cn: '黄立仓', en: 'Yellow Tip Pomfret' }
        ],
        // Column 2
        [
          { tc: '海鱸 1, 3公斤', cn: '海鲈 1, 3公斤', en: 'Sea Bass 1,3 KG' },
          { tc: '加洲鱸', cn: '加洲鲈', en: 'Local Sea Bass' },
          { tc: '盲曹', cn: '盲曹', en: 'Barramudi' },
          { tc: '魚仲(懵仔魚)', cn: '鱼仲(懵仔鱼)', en: 'Cobia' },
          { tc: '白鱔', cn: '白鳝', en: 'White Eel' },
          { tc: '鮮東星班', cn: '鲜东星班', en: 'Coral Trout' }
        ],
        // Column 3
        [
          { tc: '桂魚', cn: '桂鱼', en: 'Mandarin Fish' },
          { tc: '紅衫魚(鮮)', cn: '红衫鱼(鲜)', en: 'Golden Threadfin Bream (Chilled)' },
          { tc: '黃花魚(大)', cn: '黄花鱼(大)', en: 'Yellow Croaker (Large)' },
          { tc: '石班', cn: '石班', en: 'Rock Grouper' },
          { tc: '九肚魚', cn: '九肚鱼', en: 'Pearl Fish' },
          { tc: '鯪魚青', cn: '鲮鱼青', en: 'Crap Fish Minced' }
        ]
      ]
    },
    {
      name_tc: '急凍魚',
      name_cn: '急冻鱼',
      name_en: 'Frozen Fish',
      image: 'https://images.unsplash.com/photo-1582359425634-e3ded9a93770?w=800',
      products: [
        // Column 1
        [
          { tc: '白雪魚', cn: '白雪鱼', en: 'White COD fish' },
          { tc: '銀雪魚', cn: '银雪鱼', en: 'Black COD fish' },
          { tc: '比目魚', cn: '比目鱼', en: 'Turbot fish' },
          { tc: '凍紅魚', cn: '冻红鱼', en: 'Red Snapper (Frozen) fish' },
          { tc: '吞拿魚扒', cn: '吞拿鱼扒', en: 'Tuna Steak' },
          { tc: '刀把蘇', cn: '刀把苏', en: 'Dover Sole fish' }
        ],
        // Column 2
        [
          { tc: '白飯魚', cn: '白饭鱼', en: 'White Bait' },
          { tc: '英倉(急凍)', cn: '英仓(急冻)', en: 'Frozen Chinese Promfet' },
          { tc: '紐西蘭青衣柳', cn: '纽西兰青衣柳', en: 'Ling Fillet (New Zealand)' },
          { tc: '葡萄牙沙甸魚', cn: '葡萄牙沙甸鱼', en: 'Sardine Fish' },
          { tc: '凍馬鮫魚', cn: '冻马鲛鱼', en: 'Mackerel (Frozen)' },
          { tc: '醬油漬三文魚籽', cn: '酱油渍三文鱼籽', en: 'Salmon Roe 500g/pkt' }
        ],
        // Column 3
        [
          { tc: '煙三文', cn: '烟三文', en: 'Smoked Salmon' },
          { tc: '附味海鱸魚柳', cn: '附味海鲈鱼柳', en: 'Seabass Fillet' },
          { tc: '龍利柳', cn: '龙利柳', en: 'Sole Fillet' },
          { tc: '油甘魚柳(日本)', cn: '油甘鱼柳(日本)', en: 'Hamachi Fillet (Japan)' },
          { tc: '鯛魚柳', cn: '鲷鱼柳', en: 'Tilapia Fillet' },
          { tc: '凍盲曹柳', cn: '冻盲曹柳', en: 'Barramundi Fillet (Frozen)' }
        ]
      ]
    },
    {
      name_tc: '龍蝦',
      name_cn: '龙虾',
      name_en: 'Lobster',
      image: 'https://customer-assets.emergentagent.com/job_147163fb-d400-4158-b086-5933c41e8c02/artifacts/xkac7c5y_Screenshot%202026-04-24%20122923.png',
      products: [
        // Column 1
        [
          { tc: '波士頓龍蝦(1 / 1.5磅)', cn: '波士顿龙虾(1 / 1.5磅)', en: 'Boston Lobster (1 / 1.5 lb)' },
          { tc: '波士頓龍蝦(熟)', cn: '波士顿龙虾(熟)', en: 'Boston Lobster (Cooked)' },
          { tc: '波士頓龍蝦尾', cn: '波士顿龙虾尾', en: 'Lobster Tail (Boston)' }
        ],
        // Column 2
        [
          { tc: '龍蝦尾(澳洲)', cn: '龙虾尾(澳洲)', en: 'Lobster Tail (Australia)' },
          { tc: '澳洲龍蝦', cn: '澳洲龙虾', en: 'Lobster (Australia)' },
          { tc: '龍蝦頭', cn: '龙虾头', en: 'Lobster Head' }
        ]
      ]
    },
    {
      name_tc: '蟹',
      name_cn: '蟹',
      name_en: 'Crab',
      image: 'https://customer-assets.emergentagent.com/job_147163fb-d400-4158-b086-5933c41e8c02/artifacts/yuuqxnj2_Screenshot%202026-04-24%20123132.png',
      products: [
        // Column 1
        [
          { tc: '阿拉斯加蟹爪', cn: '阿拉斯加蟹爪', en: 'King Crab Leg' },
          { tc: '軟殼蟹 80/100gm', cn: '软壳蟹 80/100gm', en: 'Soft Shell Crab 80/100gm' },
          { tc: '軟殼蟹 120/150gm', cn: '软壳蟹 120/150gm', en: 'Soft Shell Crab 120/150gm' },
          { tc: '軟殼蟹 100/120gm', cn: '软壳蟹 100/120gm', en: 'Soft Shell Crab 100/120gm' },
          { tc: '重皮蟹 / 奄仔', cn: '重皮蟹 / 奄仔', en: 'Double Shell / Young Crab' },
          { tc: '松葉蟹腳', cn: '松叶蟹脚', en: 'Matsuba Crab Leg' }
        ],
        // Column 2
        [
          { tc: '急凍蟹件', cn: '急冻蟹件', en: 'Crab (Portion)' },
          { tc: 'JL/SL/CM 蟹冧肉(罐頭)', cn: 'JL/SL/CM 蟹冧肉(罐头)', en: 'JL/SL/CM Crab Meat (Canned)' },
          { tc: '蟹籽', cn: '蟹籽', en: 'Crab Roe' },
          { tc: '羔蟹', cn: '羔蟹', en: 'Crab Female' },
          { tc: '大閘蟹', cn: '大闸蟹', en: 'Hair Crab' },
          { tc: '蟹黃', cn: '蟹黄', en: 'Crab Roe' }
        ],
        // Column 3
        [
          { tc: 'GSL俄羅斯急凍蟹腳', cn: 'GSL俄罗斯急冻蟹脚', en: 'GSL Russian Frozen Crab Legs Size: 6-9, 9-12, 12-14' },
          { tc: '俄羅斯有殼蟹段', cn: '俄罗斯有壳蟹段', en: 'Russian Crab Clusters Size: 5-7pcs/pack' },
          { tc: '俄羅斯蟹段肉', cn: '俄罗斯蟹段肉', en: 'Russian Crab Meat Size: 7-9pcs/pack' }
        ]
      ]
    },
    {
      name_tc: '蝦',
      name_cn: '虾',
      name_en: 'Shrimp / Prawn',
      image: 'https://images.unsplash.com/photo-1674066621842-8c830e392618?w=800',
      products: [
        // Column 1
        [
          { tc: '虎蝦4/6, 7/8, 13/15, 16/20, 26/30', cn: '虎虾4/6, 7/8, 13/15, 16/20, 26/30', en: 'Tiger Prawn4/6, 7/8, 13/15, 16/20, 26/30' },
          { tc: '去頭虎蝦13/15,16/20,21/25,26/30,31/40', cn: '去头虎虾13/15,16/20,21/25,26/30,31/40', en: 'Headless Tiger Prawn13/15,16/20,21/25,26/30,31/40' },
          { tc: '蝦肉41/50,51/60,61/70,71/90', cn: '虾肉41/50,51/60,61/70,71/90', en: 'Prawn Meat41/50,51/60,61/70,71/90' },
          { tc: '蝦肉13/15,16/20,21/25,26/30,31/40', cn: '虾肉13/15,16/20,21/25,26/30,31/40', en: 'Prawn Meat13/15,16/20,21/25,26/30,31/40' },
          { tc: '鳳尾蝦16/20, 21/25, 26/30', cn: '凤尾虾16/20, 21/25, 26/30', en: 'Peeled Shrimp with Tail16/20, 21/25, 26/30' },
          { tc: '蝦肉 IQF16/20, 21/25', cn: '虾肉 IQF16/20, 21/25', en: 'Prawn Meat IQF16/20, 21/25' }
        ],
        // Column 2
        [
          { tc: '大頭蝦(越)U3, U5, U7, U10', cn: '大头虾(越)U3, U5, U7, U10', en: 'Big Head PrawnU3, U5, U7, U10 (Vietnam)' },
          { tc: '藍尾棕蝦U20', cn: '蓝尾棕虾U20', en: 'Pink PrawnU20' },
          { tc: '阿根庭紅蝦L1, L2', cn: '阿根庭红虾L1, L2', en: 'Argentinnean Red ShrimpL1, L2' },
          { tc: '西班牙紅魔蝦', cn: '西班牙红魔虾', en: 'Carabineros Shrimp' },
          { tc: '澳洲花蝦U10', cn: '澳洲花虾U10', en: 'Flower PrawnU10' },
          { tc: '熟蝦16/20, 21/25', cn: '熟虾16/20, 21/25', en: 'Cooked Prawn16/20, 21/25' }
        ],
        // Column 3
        [
          { tc: '生蝦仔', cn: '生虾仔', en: 'Shrimp (Live)' },
          { tc: '九節蝦', cn: '九节虾', en: 'Shrimp (Live)' },
          { tc: '基圍蝦', cn: '基围虾', en: 'Local Shrimp (Live)' },
          { tc: '飯蝦肉', cn: '饭虾肉', en: 'Prawn Meat For Rice' },
          { tc: '湯蝦', cn: '汤虾', en: 'Shrimp (For Soup)' },
          { tc: '蝦膠', cn: '虾胶', en: 'Shrimp Puree' }
        ]
      ]
    },
    {
      name_tc: '魷魚,墨魚同八爪魚',
      name_cn: '鱿鱼,墨鱼同八爪鱼',
      name_en: 'Squid, Cuttle Fish and Octopus',
      image: 'https://images.unsplash.com/photo-1635175001878-feed143ea72a?w=800',
      products: [
        // Column 1
        [
          { tc: '大八爪魚', cn: '大八爪鱼', en: 'Octopus Big' },
          { tc: '日本大八爪魚', cn: '日本大八爪鱼', en: 'Japanese Octopus' },
          { tc: '小八爪魚', cn: '小八爪鱼', en: 'Octopus Baby' },
          { tc: '魷魚圈', cn: '鱿鱼圈', en: 'Squid Ring' },
          { tc: '水魷魚', cn: '水鱿鱼', en: 'Preserved Squid' },
          { tc: '海膽(加拿大)', cn: '海胆(加拿大)', en: 'Sea Urchin (Canada)' }
        ],
        // Column 2
        [
          { tc: '墨魚柳', cn: '墨鱼柳', en: 'Cuttle Fish Fillet' },
          { tc: '墨魚(全清)', cn: '墨鱼(全清)', en: 'Cuttle Fish(Cleaned)' },
          { tc: '墨魚仔', cn: '墨鱼仔', en: 'Cuttle Fish Baby' },
          { tc: '墨魚', cn: '墨鱼', en: 'Cuttle Fish' },
          { tc: '墨汁', cn: '墨汁', en: 'Black Ink' },
          { tc: '墨魚膠', cn: '墨鱼胶', en: 'Cuttle Fish Puree' }
        ],
        // Column 3
        [
          { tc: '魷魚', cn: '鱿鱼', en: 'Squid' },
          { tc: '有皮魷仔', cn: '有皮鱿仔', en: 'Squid Baby w/ Skin' },
          { tc: '魷筒仔', cn: '鱿筒仔', en: 'Baby Squid Tube' },
          { tc: '急凍魷魚頭', cn: '急冻鱿鱼头', en: 'Frozen Squid Head' },
          { tc: '急凍魷筒', cn: '急冻鱿筒', en: 'SquidTube (Frozen)' },
          { tc: '去皮魷魚', cn: '去皮鱿鱼', en: 'Squid w/o Skin' }
        ]
      ]
    },
    {
      name_tc: '貝類同青口',
      name_cn: '贝类同青口',
      name_en: 'Shell and Mussel',
      image: 'https://images.unsplash.com/photo-1561727990-ad20dee8758d?w=800',
      products: [
        // Column 1
        [
          { tc: '活生青口(澳洲)', cn: '活生青口(澳洲)', en: 'Mussel (Australia, Live)' },
          { tc: '凍半殼熟青口', cn: '冻半壳熟青口', en: 'Half Shell Mussel' },
          { tc: '原凍黑青口', cn: '原冻黑青口', en: 'Black Mussel (Frozen)' },
          { tc: '翡翠螺', cn: '翡翠螺', en: 'Green Whelk' },
          { tc: '青口肉', cn: '青口肉', en: 'Mussel Meat' },
          { tc: '南非鮑魚10頭', cn: '南非鲍鱼10头', en: 'South Afr. Live Abalone' }
        ],
        // Column 2
        [
          { tc: '沙白(已吐沙)', cn: '沙白(已吐沙)', en: 'Sha Pak Clam' },
          { tc: '馬尼拉蜆', cn: '马尼拉蚬', en: 'Manila Clam(USA)' },
          { tc: '抄蜆', cn: '抄蚬', en: 'Clam' },
          { tc: '蠔仔', cn: '蚝仔', en: 'Oyster Baby' },
          { tc: '台山蠔', cn: '台山蚝', en: 'Taishan Oyster' },
          { tc: '聖子(大)', cn: '圣子(大)', en: 'Razor Clam(Large)' }
        ],
        // Column 3
        [
          { tc: '珍珠帶子', cn: '珍珠带子', en: 'Scallop' },
          { tc: '日本刺身帶子S/M/L', cn: '日本刺身带子S/M/L', en: 'Scallop S/M/L (For sushi)' },
          { tc: '中國帶子10/20', cn: '中国带子10/20', en: 'Chinese Scallop 10/20' },
          { tc: '海鮮什錦', cn: '海鲜什锦', en: 'Mixed Seafood' },
          { tc: '半殼扇貝', cn: '半壳扇贝', en: 'Half Shell Cockle' },
          { tc: '熟蜆', cn: '熟蚬', en: 'Cooked Clam' }
        ]
      ]
    },
    {
      name_tc: '肉類',
      name_cn: '肉类',
      name_en: 'Meat',
      image: 'https://images.unsplash.com/photo-1690983323238-0b91789e1b5a?w=800',
      products: [
        // Column 1
        [
          { tc: '三黃雞', cn: '三黄鸡', en: 'Three yellow Chicken' },
          { tc: '雞中翼', cn: '鸡中翼', en: 'Chicken Wing' },
          { tc: '雞脾肉', cn: '鸡脾肉', en: 'Chicken Leg Meat' },
          { tc: '春雞', cn: '春鸡', en: 'Spring Chicken' },
          { tc: '雞殼', cn: '鸡壳', en: 'Chicken Bone' },
          { tc: '免治雞肉', cn: '免治鸡肉', en: 'Minced Chicken' }
        ],
        // Column 2
        [
          { tc: '急凍紐西蘭牛西冷', cn: '急冻纽西兰牛西冷', en: 'Frozen NZ Beef Striploin' },
          { tc: '肥牛片', cn: '肥牛片', en: 'Short Beef Sliced' },
          { tc: '牛腩粒', cn: '牛腩粒', en: 'Frozen Beef Brisket' },
          { tc: '急凍牛湯骨', cn: '急冻牛汤骨', en: 'Frozen Beef Soup Bone' },
          { tc: '急凍巴西牛柳', cn: '急冻巴西牛柳', en: 'Frozen Beef Tenderloin' },
          { tc: '急凍牛尾', cn: '急冻牛尾', en: 'Frozen Ox Tail' }
        ],
        // Column 3
        [
          { tc: '有皮挑骨豬腩肉', cn: '有皮挑骨猪腩肉', en: 'Pork Belly (with skin)' },
          { tc: '豬頸肉', cn: '猪颈肉', en: 'Pork Neck Meat' },
          { tc: '豬踭', cn: '猪踭', en: 'Meat-Pork knucklet' },
          { tc: '急凍免治豬肉', cn: '急冻免治猪肉', en: 'Frozen Minced Pork' },
          { tc: '巴西豬扒', cn: '巴西猪扒', en: 'Pork chop' },
          { tc: '紐西蘭羊膝', cn: '纽西兰羊膝', en: 'New ZealandLamb Lamb Shank' }
        ]
      ]
    },
    {
      name_tc: '加工食物',
      name_cn: '加工食物',
      name_en: 'Foodstuff',
      image: 'https://images.unsplash.com/photo-1677751177812-eef0b47b888d?w=800',
      products: [
        // Column 1
        [
          { tc: '一夜乾馬友', cn: '一夜干马友', en: 'one day dry Thread Fin' },
          { tc: '冷凍田雞肶', cn: '冷冻田鸡肶', en: 'Frozen Frog Leg' },
          { tc: '日本蒲燒鰻魚', cn: '日本蒲烧鳗鱼', en: 'Japanese Eel' },
          { tc: '炸魚條', cn: '炸鱼条', en: 'Fried Fish Finger' },
          { tc: '日本黑毛和牛漢堡扒', cn: '日本黑毛和牛汉堡扒', en: 'Japan Beef Burger' },
          { tc: '潮洲白魚蛋', cn: '潮洲白鱼蛋', en: 'Chiu Chow Fish Ball' }
        ],
        // Column 2
        [
          { tc: '香茅豬扒', cn: '香茅猪扒', en: 'Lemongrass Pork Chop' },
          { tc: '韓式牛柳條', cn: '韩式牛柳条', en: 'Korea Style Beef Fillet' },
          { tc: '蒜蓉雞扒', cn: '蒜蓉鸡扒', en: 'Garlic Chicken steak' },
          { tc: '蒜蓉雞槌', cn: '蒜蓉鸡槌', en: 'Garlic Drumstick (with skin)' },
          { tc: '沙嗲牛肉片', cn: '沙嗲牛肉片', en: 'Satay Sliced Beef' },
          { tc: '泰式豬頸肉', cn: '泰式猪颈肉', en: 'Thai Style Pork Neck' }
        ],
        // Column 3
        [
          { tc: '薯條粗直', cn: '薯条粗直', en: 'French Fried (Thick strips)' },
          { tc: '薯條(幼直)', cn: '薯条(幼直)', en: 'French Fried' },
          { tc: '炸魚條', cn: '炸鱼条', en: 'Fried Fish Finger' },
          { tc: '魚肉燒賣', cn: '鱼肉烧卖', en: 'Siu Mai' },
          { tc: '日本極手延烏冬', cn: '日本极手延乌冬', en: 'Japanese Udon' },
          { tc: '紅腸', cn: '红肠', en: 'Red Sausage' }
        ]
      ]
    }
  ];

  const getCategoryName = (category) => {
    if (language === 'en') return category.name_en;
    if (language === 'cn') return category.name_cn;
    return category.name_tc;
  };

  return (
    <div className="min-h-screen">
      <SEO {...meta} path="/products" language={language} schemas={[companySchema(), productCollectionSchema(language)]} />
      <Header />

      <section className="py-20 bg-gradient-to-br from-cyan-50 to-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold mb-6 gradient-text">
              {language === 'tc' ? '我們的海鮮' : language === 'cn' ? '我们的海鲜' : 'Our Seafood'}
            </h1>
            <p className="text-xl text-slate-600">
              {language === 'tc' 
                ? '來自世界各地的優質海鮮，為您的餐桌帶來新鮮與健康。'
                : language === 'cn'
                ? '来自世界各地的优质海鲜，为您的餐桌带来新鲜与健康。'
                : 'Premium seafood from around the world, bringing freshness and health to your table.'}
            </p>
          </motion.div>

          <div className="space-y-16 max-w-7xl mx-auto">
            {categories.map((category, catIndex) => (
              <motion.div
                key={catIndex}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: catIndex * 0.1 }}
                viewport={{ once: true }}
                className="glass rounded-3xl overflow-hidden p-8"
              >
                <div className="flex flex-col gap-8">
                  {/* Category Header with Image */}
                  <div className="flex flex-col md:flex-row gap-6 items-center">
                    <div className="md:w-1/4">
                      <div className="aspect-square rounded-2xl overflow-hidden">
                        <img 
                          src={category.image} 
                          alt={getCategoryName(category)}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    </div>
                    <div className="md:w-3/4">
                      <h2 className="text-4xl font-bold gradient-text mb-4">
                        {getCategoryName(category)}
                      </h2>
                    </div>
                  </div>

                  {/* Products Grid - 3 columns x 6 rows */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {category.products.map((column, colIndex) => (
                      <div key={colIndex} className="space-y-3">
                        {column.map((product, prodIndex) => (
                          <motion.div
                            key={prodIndex}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: prodIndex * 0.05 }}
                            viewport={{ once: true }}
                            className="flex items-start text-slate-700"
                          >
                            <span className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                            <span className="text-sm">
                              <strong>{getLocalizedText(product)}</strong>
                              <br />
                              <span className="text-xs text-slate-500">
                                {language === 'en' ? (product.tc || product.cn) : product.en}
                              </span>
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
      <Footer />
    </div>
  );
};

export default ProductsPage;
